<script setup lang="ts">
import IconPlus from '~icons/mdi/plus'
import IconEmptyList from '~icons/mdi/list-box-outline'

interface ShoppingList {
  id: string
  name: string
  selected: boolean
  readonly?: boolean
}

const props = defineProps<{
  list: ShoppingList[]
}>()

const emit = defineEmits<{
  selectList: [list: ShoppingList]
  addItem: [name: string]
}>()

const newItemName = ref('')
const isInputDisabled = computed(() => !newItemName.value.trim())

function toggleSelection(list: ShoppingList) {
  if (list.readonly)
    return
  emit('selectList', {
    ...list,
    selected: !list.selected,
  })
}

function addNewItem() {
  if (newItemName.value.trim()) {
    emit('addItem', newItemName.value.trim())
    newItemName.value = ''
  }
}
</script>

<template>
  <div class="shopping-list-item-container">
    <div
      v-if="props.list.length === 0"
      class="text-center py-8"
    >
      <v-icon :icon="IconEmptyList" size="48" class="text-grey" />
      <p class="text-grey">
        {{ $t('shoppingList.emptyList') }}
      </p>
    </div>
    <div
      v-for="item in props.list"
      :key="item.id"
      class="shopping-list-item my-2"
      :class="{ selected: item.selected, readonly: item.readonly }"
      @click="toggleSelection(item)"
    >
      <div class="d-flex align-center justify-space-between px-4">
        <span class="list-name">{{ item.name }}</span>
        <v-checkbox
          :model-value="item.selected"
          hide-details
          density="compact"
          color="primary"
          :readonly="item.readonly"
          :disabled="item.readonly"
          @click.stop
          @update:model-value="() => toggleSelection(item)"
        />
      </div>
    </div>
  </div>

  <div class="border-t border-grey-2 pt-4 mt-2">
    <div class="d-flex align-center">
      <v-text-field
        v-model="newItemName"
        :placeholder="$t('shoppingList.addNewList')"
        hide-details
        density="compact"
        rounded="lg"
        variant="outlined"
        class="mr-2 mb-0"
        data-test-id="shopping-list-add-list-input"
        @keyup.enter="addNewItem"
      />
      <v-btn
        :prepend-icon="IconPlus"
        color="primary"
        :disabled="isInputDisabled"
        data-test-id="shopping-list-add-list-button"
        data-track-id="shopping_list_add_list"
        @click="addNewItem"
      >
        {{ $t('add') }}
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.shopping-list-item-container {
  max-height: calc(100vh - 780px);
  min-height: 150px;
  overflow-y: auto;
}

.shopping-list-item {
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &.selected {
    background-color: #f0f0f7;

    :deep(.v-selection-control__input) {
      .v-selection-control__control {
        background-color: rgb(103, 103, 145);
        border-radius: 50%;
      }
    }
  }

  &.readonly {
    opacity: 0.9;
  }

  .list-name {
    font-size: 1rem;
    color: rgba(0, 0, 0, 0.87);
  }
}

.add-shopping-list {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
